import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import {
  Badge,
  Flex,
  Text,
  Skeleton,
  Alert,
  AlertDescription,
  Icon,
} from '@kognitos/lattice';
import { AssetPublishDialog } from '@/layouts/AssetWorkspaceLayout';
import type { V1CredentialInfo } from '@/generated/hooks/grimoire/models';
import DraftPublishDialogConnectionItem from './DraftPublishDialogConnectionItem';
import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import type { IDraftPublishDialogProps } from '../types';
import { getNextMajorVersion } from '@/shared/utils/version-utils';

function DraftPublishDialog({
  open,
  onClose,
  displayName,
  requiresAuthentication,
  requiresProductionCredentials,
  draftVersion,
  collectionsToPublish,
  isLoadingCollections,
  isCollectionsError,
  publishedBy,
  onSelectConnectionClick,
  onAddProdConnection,
  onAddNewConnection,
  onPublish,
  isValidating,
  isValidationError,
  isPublishing,
  selectedConnectionIds,
}: IDraftPublishDialogProps) {
  const { t: tDrafts } = useTranslation('drafts');
  const { t: tCommon } = useTranslation('common');

  // Calculate credential info arrays and counts
  const { testAndProductionCredentialsInfo, productionCredentialInfos } =
    useMemo(() => {
      const renderCredentialInfo = (
        credentialInfo: V1CredentialInfo,
        idx: number,
        showExistingConnections?: boolean,
        isProductionOnly?: boolean,
      ) => {
        let selectedConnectionId;
        if (isProductionOnly) {
          selectedConnectionId =
            selectedConnectionIds?.missing_prod_creds?.[idx];
        } else {
          selectedConnectionId =
            selectedConnectionIds?.missing_test_and_prod_creds?.[idx];
        }
        if (selectedConnectionId) {
          return null;
        }
        return (
          <DraftPublishDialogConnectionItem
            key={credentialInfo.book + credentialInfo.book_version + idx}
            credentialInfo={credentialInfo}
            onSelectConnectionClick={(integration: Bdkv2BookDescriptor) =>
              onSelectConnectionClick({ credentialInfo, idx, integration })
            }
            onAddProdConnection={(integration: Bdkv2BookDescriptor) =>
              onAddProdConnection({ credentialInfo, idx, integration })
            }
            onAddNewConnection={(integration: Bdkv2BookDescriptor) =>
              onAddNewConnection({ credentialInfo, idx, integration })
            }
            showExistingConnections={showExistingConnections}
          />
        );
      };

      const testAndProdInfos =
        requiresAuthentication
          ?.map((credentialInfo, idx) =>
            renderCredentialInfo(credentialInfo, idx, true, false),
          )
          .filter(Boolean) || [];

      const prodInfos =
        requiresProductionCredentials
          ?.map((credentialInfo, idx) =>
            renderCredentialInfo(credentialInfo, idx, false, true),
          )
          .filter(Boolean) || [];

      return {
        testAndProductionCredentialsInfo: testAndProdInfos,
        productionCredentialInfos: prodInfos,
      };
    }, [
      requiresAuthentication,
      requiresProductionCredentials,
      selectedConnectionIds,
      onSelectConnectionClick,
      onAddProdConnection,
      onAddNewConnection,
    ]);

  const totalAttentionCount =
    testAndProductionCredentialsInfo.length + productionCredentialInfos.length;

  const hasRequirements = totalAttentionCount > 0;

  const renderContent = () => {
    // Validation failed, so we don't know what the publish needs — the alert
    // below carries the message and Publish stays disabled.
    if (isValidationError && !isValidating) {
      return null;
    }

    if (isValidating) {
      return (
        <Flex direction="column" gap={1.5} width="full">
          <Skeleton className="w-1/3 h-4" />
          <Skeleton className="w-full h-20" />
          <Skeleton className="w-1/3 h-4" />
          <Skeleton className="w-full h-20" />
        </Flex>
      );
    }

    if (!hasRequirements) {
      return null;
    }

    // When there are requirements, show connection items
    return (
      <>
        {testAndProductionCredentialsInfo.length > 0 && (
          <Flex direction="column" gap={1.5} width="full">
            <Flex gap={2.5} align="center" width="full">
              <Text level="small" weight="medium">
                {tDrafts('drafts.publishDialog.section.requiresConnection')}
              </Text>
              <Badge variant="secondary" className="rounded-full">
                {testAndProductionCredentialsInfo.length}
              </Badge>
            </Flex>
            <Flex direction="column" gap={1.5} width="full">
              {testAndProductionCredentialsInfo}
            </Flex>
          </Flex>
        )}

        {productionCredentialInfos.length > 0 && (
          <Flex direction="column" gap={1.5} width="full">
            <Flex gap={2.5} align="center" width="full">
              <Text level="small" weight="medium">
                {tDrafts(
                  'drafts.publishDialog.section.requiresProductionCredentials',
                )}
              </Text>
              <Badge variant="secondary" className="rounded-full">
                {productionCredentialInfos.length}
              </Badge>
            </Flex>
            <Flex direction="column" gap={1.5} width="full">
              {productionCredentialInfos}
            </Flex>
          </Flex>
        )}
      </>
    );
  };

  const renderDescription = () => {
    if (isValidating) {
      return (
        <>
          <Flex direction="column" gap={2} width="full" className="mt-2">
            <Text className="sr-only">{tCommon('common.loading')}</Text>
            <Skeleton className="w-full h-4" />
            <Skeleton className="w-1/2 h-4" />
          </Flex>
          <Skeleton className="w-full h-4" />
        </>
      );
    }
    return (
      <>
        <Text level="small" color="muted">
          {tDrafts('drafts.publishDialog.description')}
        </Text>
        {totalAttentionCount > 0 && (
          <Text level="small" color="muted">
            {tDrafts('drafts.publishDialog.description.attention', {
              count: totalAttentionCount,
            })}
          </Text>
        )}
      </>
    );
  };

  const showsVersion = !isValidating && !isValidationError && !hasRequirements;

  return (
    <AssetPublishDialog
      open={open}
      onClose={onClose}
      title={tDrafts('drafts.publishDialog.title', { displayName })}
      description={renderDescription()}
      nextVersion={
        showsVersion ? getNextMajorVersion(draftVersion || '0') : undefined
      }
      onPublish={() => onPublish?.()}
      isPublishing={isPublishing}
      publishDisabled={
        isValidating ||
        isValidationError ||
        hasRequirements ||
        isLoadingCollections ||
        isCollectionsError ||
        // Publishing collections needs the publisher identity, still loading.
        (!!collectionsToPublish?.length && !publishedBy)
      }
    >
      {renderContent()}

      {isValidationError && !isValidating && (
        <Alert variant="destructive">
          <AlertDescription>
            {tDrafts('drafts.publishDialog.error.validationFailed')}
          </AlertDescription>
        </Alert>
      )}

      {isCollectionsError && (
        <Alert variant="destructive">
          <AlertDescription>
            {tDrafts('drafts.publishDialog.error.collectionsLoadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {collectionsToPublish && collectionsToPublish.length > 0 && (
        <Flex direction="column" gap={3} width="full">
          <Flex direction="column" gap={1} width="full">
            <Flex gap={3} align="center" width="full">
              <Text level="base" weight="medium">
                {tDrafts('drafts.publishDialog.section.collections')}
              </Text>
              <Badge variant="secondary" className="rounded-full">
                {collectionsToPublish.length}
              </Badge>
            </Flex>
            <Text level="small" color="muted">
              {tDrafts('drafts.publishDialog.section.collections.description', {
                count: collectionsToPublish.length,
              })}
            </Text>
          </Flex>
          <Flex
            direction="column"
            gap={1}
            width="full"
            className="max-h-56 overflow-y-auto pr-1"
          >
            {collectionsToPublish.map(collection => (
              <Flex key={collection.id} gap={2} align="center" width="full">
                <Icon
                  type="Database"
                  size="sm"
                  className="text-muted-foreground shrink-0"
                />
                <Text level="small" className="truncate">
                  {collection.displayName}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
    </AssetPublishDialog>
  );
}

export default DraftPublishDialog;
